import React, { createContext, useState, useEffect, ReactNode, useMemo } from "react";
import { ConfigProvider } from "antd";
import { ThemeProvider } from "styled-components";
import CustomTheme from "config/themeColor";
import { getAntTheme, getColor, getComponent } from "config/ThemeVariable";

type ThemeType = "SYSTEM_PREFRENCE" | "LIGHT" | "DARK";

interface ThemeContextProps {
  theme: ThemeType;
  setTheme: (theme: ThemeType) => void;
}

interface ThemeProviderWrapperProps {
  children: ReactNode;
}

const ThemeContext = createContext<ThemeContextProps>({
  theme: "SYSTEM_PREFRENCE",
  setTheme: () => {},
});

const ThemeProviderWrapper: React.FC<ThemeProviderWrapperProps> = ({
  children,
}) => {
  const getSystemTheme = (): ThemeType => {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "DARK"
      : "LIGHT";
  };

  const [theme, setTheme] = useState<ThemeType>(
    (localStorage.getItem("theme") as ThemeType) || "SYSTEM_PREFRENCE"
  );
  const [systemTheme, setSystemTheme] = useState<ThemeType>(getSystemTheme());

  useEffect(() => {
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    if (theme === "SYSTEM_PREFRENCE") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

      const handleSystemThemeChange = (e: MediaQueryListEvent) => {
        setSystemTheme(e.matches ? "DARK" : "LIGHT");
      };

      mediaQuery.addEventListener("change", handleSystemThemeChange);
      return () =>
        mediaQuery.removeEventListener("change", handleSystemThemeChange);
    }
  }, [theme]);

  let currentTheme;
  if (theme === "DARK") {
    currentTheme = CustomTheme.DarkMode;
  } else if (theme === "LIGHT") {
    currentTheme = CustomTheme.Default;
  } else if (systemTheme === "DARK") {
    currentTheme = CustomTheme.DarkMode;
  } else {
    currentTheme = CustomTheme.Default;
  }

  const contextValue = useMemo(
    () => ({ theme, setTheme }),
    [theme, setTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={getColor(currentTheme)}>
        <ConfigProvider
          theme={{
            token: getAntTheme(currentTheme),
            components: getComponent(currentTheme),
          }}
        >
          {children}
        </ConfigProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export { ThemeContext, ThemeProviderWrapper };
